
import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'login' | 'register';
  onSwitchMode: (mode: 'login' | 'register') => void;
}

export default function AuthModal({ isOpen, onClose, mode, onSwitchMode }: AuthModalProps) {
  const { login, register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'register') {
        if (formData.password !== formData.confirmPassword) {
          setError('Las contraseñas no coinciden');
          return;
        }
        if (formData.password.length < 6) {
          setError('La contraseña debe tener al menos 6 caracteres');
          return;
        }
        if (!formData.fullName.trim()) {
          setError('El nombre completo es obligatorio');
          return;
        }
        
        await register(formData.email, formData.password, formData.fullName);
      } else {
        await login(formData.email, formData.password);
      }
      
      // Limpiar formulario y cerrar modal
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        fullName: ''
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error en la autenticación. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="w-full max-w-sm vintage-paper aged-paper shadow-xl relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center vintage-paper-subtle hover:bg-sepia-200/70 transition-all duration-200 rounded-full"
          >
            <i className="ri-close-line text-sepia-700"></i>
          </button>

          {/* Header */}
          <div className="p-6 pb-4">
            <h2 className="font-headings text-xl text-sepia-900 mb-2">
              {mode === 'login' ? 'INICIAR SESIÓN' : 'REGISTRARSE'}
            </h2>
            <p className="text-sm text-sepia-600 font-body">
              {mode === 'login' 
                ? 'Accede a tu cuenta para comentar y crear contenido' 
                : 'Únete a nuestra comunidad literaria'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-ui text-sepia-700 mb-2">
                  Nombre completo
                </label>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Tu nombre completo"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 text-sm font-body text-sepia-800 bg-lp-paper-2/60 border border-sepia-400/40 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50 focus:bg-lp-paper-2/80 vintage-paper-subtle"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-ui text-sepia-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                placeholder="Tu correo electrónico"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full p-3 text-sm font-body text-sepia-800 bg-lp-paper-2/60 border border-sepia-400/40 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50 focus:bg-lp-paper-2/80 vintage-paper-subtle"
              />
            </div>

            <div>
              <label className="block text-sm font-ui text-sepia-700 mb-2">
                Contraseña
              </label>
              <input
                type="password"
                name="password"
                placeholder="Tu contraseña"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="w-full p-3 text-sm font-body text-sepia-800 bg-lp-paper-2/60 border border-sepia-400/40 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50 focus:bg-lp-paper-2/80 vintage-paper-subtle"
              />
            </div>

            {mode === 'register' && (
              <div>
                <label className="block text-sm font-ui text-sepia-700 mb-2">
                  Confirmar contraseña
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirma tu contraseña"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 text-sm font-body text-sepia-800 bg-lp-paper-2/60 border border-sepia-400/40 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-500/50 focus:bg-lp-paper-2/80 vintage-paper-subtle"
                />
              </div>
            )}

            {error && (
              <div className="vintage-paper-subtle p-3 rounded-[10%] border-l-4 border-red-400">
                <p className="text-sm text-red-700 font-ui">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-4 py-3 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Procesando...
                </>
              ) : (
                <>
                  <i className={mode === 'login' ? 'ri-login-box-line' : 'ri-user-add-line'}></i>
                  {mode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
                </>
              )}
            </button>

            {/* Switch Mode */}
            <div className="text-center pt-4 border-t border-sepia-300/50">
              <p className="text-sm text-sepia-600 font-ui">
                {mode === 'login' ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}
                <button
                  type="button"
                  onClick={() => onSwitchMode(mode === 'login' ? 'register' : 'login')}
                  className="ml-2 text-sepia-800 hover:text-sepia-900 font-medium underline"
                >
                  {mode === 'login' ? 'Regístrate' : 'Inicia sesión'}
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
